import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { PusherService } from './pusher.service';

/**
 * Servicio centralizado para gestionar las suscripciones Pusher del chat.
 * Encapsula la lógica de conexión/desconexión a canales privados y emite
 * eventos mediante Subjects RxJS para desacoplar la capa de transporte
 * en tiempo real de los componentes de UI.
 *
 * Canales gestionados:
 * - `MessageSent.{userId}` → nuevos mensajes (.message-sent / MessageSent)
 * - `ConversationsListUpdated.{userId}` → cambios en la bandeja (catch-all)
 */
@Injectable({ providedIn: 'root' })
export class ChatPusherService {
  // ── Subjects públicos ────────────────────────────────────────────────
  /** Se emite cuando llega un nuevo mensaje al canal MessageSent */
  readonly onMessageReceived$ = new Subject<any>();
  /** Se emite cuando se elimina una conversación (reservado para uso futuro) */
  readonly onConversationDeleted$ = new Subject<any>();
  /** Se emite cuando un chat se marca como leído (reservado para uso futuro) */
  readonly onChatRead$ = new Subject<any>();
  /** Se emite cuando la lista de conversaciones cambia (canal ConversationsListUpdated) */
  readonly onConversationUpdated$ = new Subject<{ eventName: string; data: any }>();

  // ── Estado interno ───────────────────────────────────────────────────
  private pusherChannel: any = null;
  private isSubscribed = false;
  private currentUserId: number | null = null;
  private retryTimeout: any = null;

  constructor(
    private pusherService: PusherService,
  ) {}

  // ── API pública ──────────────────────────────────────────────────────

  /** Indica si las suscripciones Pusher están activas */
  get subscribed(): boolean {
    return this.isSubscribed;
  }

  /**
   * Inicializa las suscripciones Pusher para el usuario indicado.
   * Si ya hay una suscripción activa no vuelve a conectar.
   *
   * @param userId - ID del usuario autenticado para nombrar los canales
   */
  async initPusherSubscription(userId: number): Promise<void> {
    if (this.isSubscribed) {
      return;
    }

    if (!userId) {
      return;
    }

    this.currentUserId = userId;

    try {
      // Inicializar el servicio Pusher
      await this.pusherService.init();

      // ── Canal de mensajes enviados ──────────────────────────────────
      const messageChannel = `MessageSent.${userId}`;

      const messageCallback = (data: any) => {
        if (!data) {
          return;
        }
        this.onMessageReceived$.next(data);
      };

      // Limpiar bindings previos
      try {
        this.pusherService.unsubscribeFromEvent(`private-${messageChannel}`, '.message-sent');
        this.pusherService.unsubscribeFromEvent(`private-${messageChannel}`, '.MessageSent');
        this.pusherService.unsubscribeFromEvent(`private-${messageChannel}`, 'MessageSent');
      } catch (_) { /* silencioso */ }

      // Escuchar variante kebab-case (.message-sent)
      this.pusherChannel = this.pusherService.listenPrivate(
        messageChannel,
        '.message-sent',
        messageCallback
      );

      // Escuchar variante PascalCase (MessageSent) – Laravel Broadcasting
      this.pusherService.listenPrivate(
        messageChannel,
        'MessageSent',
        messageCallback
      );

      // ── Canal de actualizaciones de bandeja ─────────────────────────
      const conversationsChannel = `ConversationsListUpdated.${userId}`;
      this.pusherService.forceDisconnectChannel(conversationsChannel);
      this.pusherService.listenPrivateAll(conversationsChannel, (eventName: string, eventData: any) => {
        this.onConversationUpdated$.next({ eventName, data: eventData });
      });

      this.isSubscribed = !!this.pusherChannel;
    } catch (_) {
      this.isSubscribed = false;

      // Reintentar tras 2 s
      this.retryTimeout = setTimeout(() => {
        if (!this.isSubscribed && this.currentUserId) {
          this.initPusherSubscription(this.currentUserId);
        }
      }, 2000);
    }
  }

  /**
   * Desconecta todas las suscripciones Pusher del chat y limpia el estado.
   * Seguro de llamar varias veces sin efectos adversos.
   *
   * @param userId - (opcional) ID del usuario; si no se pasa, usa el último conocido
   */
  disconnectFromPusher(userId?: number): void {
    const id = userId ?? this.currentUserId;

    // Cancelar reintento pendiente
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }

    try {
      if (this.pusherChannel) {
        this.pusherChannel = null;
      }

      if (id) {
        const messageChannel = `MessageSent.${id}`;

        // Desconectar canal de mensajes
        if (this.pusherService.echo) {
          this.pusherService.leaveThisChannel(`private-${messageChannel}`);
        }
        this.pusherService.forceDisconnectChannel(messageChannel);

        this.pusherService.unsubscribeFromEvent(`private-${messageChannel}`, '.message-sent');
        this.pusherService.unsubscribeFromEvent(`private-${messageChannel}`, '.MessageSent');
        this.pusherService.unsubscribeFromEvent(`private-${messageChannel}`, 'MessageSent');

        // Desconectar canal de lista de conversaciones
        const conversationsChannel = `ConversationsListUpdated.${id}`;
        if (this.pusherService.echo) {
          this.pusherService.leaveThisChannel(`private-${conversationsChannel}`);
        }
        this.pusherService.forceDisconnectChannel(conversationsChannel);
      }

      this.isSubscribed = false;
    } catch (_) {
      this.isSubscribed = false;
    }
  }
}
